import { copyToClipboard } from '@ctw/shared/components/copy-to-clipboard';
import { type ToastProps, notify } from '@ctw/shared/components/toast';
import type { HeadingLevel } from '@ctw/shared/utils/cda/heading-level';
import { tw, twx } from '@ctw/shared/utils/tailwind';
import { faLink } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { ReactNode } from 'react';

interface HeadingDeepLinkDefintion {
  url: string;
  onSuccessToast?: ToastProps;
}

export type HeadingProps = {
  className?: string;
  id?: string;
  testId?: string;
  children: ReactNode;
  level: HeadingLevel;
  deepLink?: HeadingDeepLinkDefintion;
  dataSearchIndex?: string;
};

export const Heading = ({
  className,
  id,
  testId,
  children,
  level,
  deepLink,
  dataSearchIndex,
}: HeadingProps) => {
  const HeadingElement = level;

  return deepLink ? (
    <a
      href={deepLink.url}
      aria-label={deepLink.onSuccessToast?.title ?? 'Copy link to clipboard'}
      data-search-index={dataSearchIndex}
      className={tw`group flex h-full w-full items-center gap-2`}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();

        void copyToClipboard(deepLink.url, () => {
          if (deepLink.onSuccessToast) {
            notify(deepLink.onSuccessToast);
          }
        });
      }}
    >
      <HeadingElement
        id={id}
        data-testid={testId}
        className={twx(
          'capitalize leading-9',
          {
            'font-bold text-2xl': level === 'h1',
            'font-medium text-xl': level === 'h2',
            'font-medium text-lg': level === 'h3',
            'font-medium text-sm': level === 'h4',
          },
          'transition-all duration-200 hover:text-primary-hover',
          className,
        )}
      >
        {children}
        <FontAwesomeIcon
          className={twx(
            'h-5 w-5 pl-2 opacity-50 transition-all duration-200 group-hover:opacity-100',
          )}
          icon={faLink}
          aria-hidden={true}
        />
      </HeadingElement>
    </a>
  ) : (
    <HeadingElement
      id={id}
      data-testid={testId}
      className={twx(
        'capitalize leading-9',
        {
          'font-bold text-2xl': level === 'h1',
          'font-medium text-xl': level === 'h2',
          'font-medium text-lg': level === 'h3',
          'font-medium text-sm': level === 'h4',
        },
        className,
      )}
    >
      {children}
    </HeadingElement>
  );
};
